import { ArrowRight } from '@mui/icons-material'
import DraftsIcon from '@mui/icons-material/Drafts'
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread'
import {
  Button,
  CircularProgress,
  IconButton,
  Link,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  Stack,
  Tooltip
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  useMarkNotificationAsUnreadMutation
} from '../../redux/api/notificationApi'
import {
  NotificationEntry,
  NotificationInfoModifiedEntityEnum
} from '../../sdk/models'
import { formatTimeFromNow } from '../../utils/formatTimeDuration/formatTimeDuration'
import { getUrl } from '../../utils/getNotificationUrl'
import { CenteredLoading } from '../CenteredLoading/CenteredLoading'
import { NoResultListItem } from '../SharedUI/LoadingListItem'
import { NOTIFICATION_PAGE_SIZE } from './useGetNotifications'
function getNotificationText(notification: NotificationEntry) {
  if (
    notification.info!.modifiedEntity ===
    NotificationInfoModifiedEntityEnum.CustomerMessage
  ) {
    return notification.info!.description
  }
  return notification.info!.title + ': ' + notification.info!.description
}

export const NotificationList = ({
  notifications,
  numUnreadNotifications,
  loadingNotifications,
  fetchingNotifications,
  setNotificationEndTime,
  notificationAnchorEl,
  setNotificationAnchorEl,
  isDvi
}: {
  notifications: NotificationEntry[] | undefined
  numUnreadNotifications: number | undefined
  loadingNotifications: boolean
  fetchingNotifications: boolean
  setNotificationEndTime: React.Dispatch<React.SetStateAction<Date | undefined>>
  notificationAnchorEl: HTMLElement | null
  setNotificationAnchorEl: (
    value: React.SetStateAction<HTMLElement | null>
  ) => void
  isDvi: boolean | undefined
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const [markAsRead, { isLoading: updatingMarkAsRead }] =
    useMarkNotificationAsReadMutation()
  const [markAsUnread, { isLoading: updatingMarkAsUnread }] =
    useMarkNotificationAsUnreadMutation()
  const [markAllAsRead, { isLoading: updatingMarkAllAsRead }] =
    useMarkAllNotificationsAsReadMutation()
  const isUpdating =
    updatingMarkAsRead || updatingMarkAsUnread || updatingMarkAllAsRead
  return (
    <Menu
      id="menu-appbar"
      anchorEl={notificationAnchorEl}
      open={Boolean(notificationAnchorEl)}
      onClose={() => {
        setNotificationAnchorEl(null)
        setNotificationEndTime(undefined)
      }}>
      <ListItem divider disablePadding>
        <Stack
          direction="row"
          spacing={1}
          sx={{ width: '100%', justifyContent: 'flex-end' }}>
          {fetchingNotifications && <CircularProgress size="1.5rem" />}
          <Button
            variant="text"
            disabled={
              isUpdating || !notifications || notifications.length === 0
            }
            onClick={() => {
              markAllAsRead({
                authorization: accessToken
              })
            }}>
            {`Mark all as read (${numUnreadNotifications})`}
          </Button>
          <Button
            variant="text"
            disabled={
              isUpdating ||
              !notifications ||
              notifications.length < NOTIFICATION_PAGE_SIZE
            }
            endIcon={<ArrowRight />}
            onClick={() => {
              if (notifications) {
                setNotificationEndTime(
                  new Date(
                    notifications[notifications.length - 1].info!.createdAt!
                  )
                )
              }
            }}>
            Next
          </Button>
        </Stack>
      </ListItem>
      {loadingNotifications && <CenteredLoading />}

      {!loadingNotifications && (
        <NoResultListItem
          noResult={!!notifications && notifications.length === 0}
        />
      )}
      {!loadingNotifications &&
        notifications?.map((notification) => (
          <ListItem dense key={notification.id!}>
            <ListItemText
              sx={{ width: '300px' }}
              primary={
                <Link
                  component={RouterLink}
                  onClick={() => {
                    markAsRead({
                      authorization: accessToken,
                      requestBody: [notification.id!]
                    })
                  }}
                  to={getUrl(notification, !!isDvi)}>
                  {getNotificationText(notification)}
                </Link>
              }
              secondary={formatTimeFromNow(notification.info!.createdAt!)}
            />
            <ListItemSecondaryAction>
              {notification.info!.readAt ? (
                <IconButton
                  edge="end"
                  onClick={() =>
                    markAsUnread({
                      authorization: accessToken,
                      requestBody: [notification.id!]
                    })
                  }>
                  <Tooltip title="Mark as unread">
                    <DraftsIcon />
                  </Tooltip>
                </IconButton>
              ) : (
                <IconButton
                  edge="end"
                  color="error"
                  onClick={() =>
                    markAsRead({
                      authorization: accessToken,
                      requestBody: [notification.id!]
                    })
                  }>
                  <Tooltip title="Mark as read">
                    <MarkEmailUnreadIcon />
                  </Tooltip>
                </IconButton>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
    </Menu>
  )
}
